import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function DemoPage() {
  const [submitted, setSubmitted] = React.useState(false);

  if (submitted) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-24 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#2ECC9A]/15">
          <CheckCircle2 className="h-7 w-7 text-[#2ECC9A]" />
        </div>
        <h1 className="mt-6 font-serif text-4xl font-bold tracking-tight text-[#1A3A5C]">
          Thanks — we&rsquo;ll be in touch within one business day.
        </h1>
        <p className="mt-3 text-slate-600">
          Meanwhile, feel free to explore the product with sample data.
        </p>
        <div className="mt-8 flex justify-center gap-3">
          <Link to="/app">
            <Button className="h-11 bg-[#1A3A5C] px-6 text-white hover:bg-[#1A3A5C]/90">Explore the product</Button>
          </Link>
          <Link to="/pricing">
            <Button variant="outline" className="h-11 border-[#1A3A5C]/20 px-6">See pricing</Button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-6 py-16">
      <div className="grid gap-12 md:grid-cols-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#2ECC9A]">Book a demo</div>
          <h1 className="mt-3 font-serif text-4xl font-bold tracking-tight text-[#1A3A5C]">
            30 minutes. Tailored to your practice.
          </h1>
          <p className="mt-4 text-slate-600">
            We&rsquo;ll bring: a live matter walkthrough, occupation-body mapping demo, OMARA compliance map, three-way trust reconciliation demo, and a data-migration checklist.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-slate-700">
            {[
              'Live product tour with your visa mix',
              'Trust accounting compliance check',
              'Migration plan from your current tool',
              'Pricing and rollout timeline',
            ].map((li) => (
              <li key={li} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#2ECC9A]" /> {li}
              </li>
            ))}
          </ul>
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardContent className="p-6">
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(true);
              }}
            >
              <div>
                <Label htmlFor="name">Your name</Label>
                <Input id="name" required placeholder="Priya Nair" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="email">Work email</Label>
                <Input id="email" type="email" required placeholder="priya@nairmigration.com.au" className="mt-1" />
              </div>
              <div>
                <Label htmlFor="firm">Firm</Label>
                <Input id="firm" required placeholder="Nair Migration Advisory" className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Practice size</Label>
                  <Select>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="solo">Solo</SelectItem>
                      <SelectItem value="2-5">2–5 staff</SelectItem>
                      <SelectItem value="6-25">6–25 staff</SelectItem>
                      <SelectItem value="25+">25+ staff</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Current tool</Label>
                  <Select>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mm">Migration Manager</SelectItem>
                      <SelectItem value="spreadsheet">Spreadsheets / email</SelectItem>
                      <SelectItem value="clio">Clio / generic legal tool</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="notes">What would you like to see?</Label>
                <Textarea id="notes" rows={3} placeholder="e.g. High-volume 482 sponsorship workflow, trust reconciliation" className="mt-1" />
              </div>
              <Button type="submit" className="h-11 w-full bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">
                Request demo
              </Button>
              <p className="text-center text-xs text-slate-400">We reply within 1 business day · no obligation</p>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
